import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useMemo,
} from 'react';
import { useApolloClient } from '../../hooks/useApolloClient';
import useDebounce from '../../hooks/useDebounce';
import { useRouteInformation } from '../../contexts/RouteInformationContext';
import { useAxisData } from '../../contexts/AxisDataContext';
import { sendEvent } from '../../contexts/AnalyticsTrackingContext';
import notify from '../../lib/notify';
import {
  getResultType,
  mergeEntitySearchResults,
  mergeFacetSearchResults,
  transformFacetSearchResult,
} from '../../lib/search';

import searchAutocomplete from '../../queries/searchAutocomplete';
import querySearchEntities from '../../queries/searchEntities';
import querySearchFacets from '../../queries/searchEventsAndRegulations';
import queryAiSuggestions from '../../queries/getAiSuggestions';

import SearchBar from './components/SearchBar/SearchBar';
import SearchFilters from './components/SearchFilters/SearchFilters';
import SearchResults from './components/SearchResults/SearchResults';

import css from './Search.module.css';
import SearchHeader from './components/SearchHeader/SearchHeader';
import useSearchCounts from '../../hooks/useSearchCounts';
import { flattenTree } from '../../utils';
import useNavigation from '../../v2/hooks/useNavigation';
import ToggleSideNav from '../Feed/components/ToggleSidenav/ToggleSidenav';
import SidebarHeader from '../Feed/components/SidebarHeader/SidebarHeader';
import { useSharedState } from '../../contexts/SharedStateContext';

const DEBOUNCE_TIME = 600;

const SearchContext = createContext();

const getFiltersSavedOnSessionStorageByQueryId = (queryId, type) => {
  if (!queryId) return [];

  const savedFilters = sessionStorage.getItem(queryId);

  try {
    const parsed = JSON.parse(savedFilters);
    return parsed[type] ?? [];
  } catch (_) {
    return [];
  }
};

const fetchAutocomplete = ({
  client,
  term,
  symbol,
  topics,
  entities,
  limit = 20,
}) => {
  return client
    .query({
      query: searchAutocomplete,
      variables: {
        term,
        symbol,
        topics,
        entities,
        limit,
      },
    })
    .then(({ data }) => data.autoCompleteQuery);
};

const fetchAiSuggestions = ({ client, term }) => {
  return client
    .query({
      query: queryAiSuggestions,
      variables: {
        term,
      },
    })
    .then(({ data }) => data.getSuggestions?.entities);
};

const fetchSearchEntityResults = ({
  client,
  name,
  topics,
  filterIndustries,
  filterEntities,
  filterCategories,
  bookmarked = false,
  connected = false,
  notes = false,
  compliance = false,
}) => {
  return client
    .query({
      query: querySearchEntities,
      variables: {
        name,
        topics,
        filterIndustries,
        filterEntities,
        filterCategories,
        bookmarked,
        connected,
        notes,
        compliance,
      },
    })
    .then(({ data }) => data.searchEntities.categories);
};

const fetchSearchFacetResults = ({
  client,
  name,
  topics,
  filterIndustries,
  filterEntities,
  filterCategories,
  startDate,
  endDate,
  bookmarked = false,
  connected = false,
  notes = false,
  compliance = false,
}) => {
  return client
    .query({
      query: querySearchFacets,
      variables: {
        name,
        topics,
        filterIndustries,
        filterEntities,
        filterCategories,
        startDate,
        endDate,
        bookmarked,
        connected,
        notes,
        compliance,
      },
    })
    .then(({ data }) => data.searchEventsAndRegulations);
};

const Search = () => {
  const client = useApolloClient();
  const router = useRouteInformation();
  const searchQuery = router.query.get('search');
  const dossierType = router.query.get('dossierType');
  const searchQueryId = router.query.get('queryId');
  const { topicId } = router.params;
  const { subscribedCountries, briefs } = useAxisData();
  const { params, replaceParams, deleteParam } = useNavigation();
  const { selectedSavedSearch, setSelectedSavedSearch } = useSharedState();

  // Search input + Debounced search input
  const [searchText, setSearchText] = useState(searchQuery || '');
  const debounceSearchText = useDebounce(searchText, DEBOUNCE_TIME);

  /*
   * These are our current search filters
   * "searchText" is the text in the search box (a string)
   * "searchCountries" is an array of { id, name } object for countries
   * "searchIndustries" is an array of { id, name } objects for briefs
   * "searchTypes" is an array of entity/facet types
   * "searchDates" is an array of [start, end] dates
   */
  const [activeSearch, setActiveSearch] = useState(searchText);
  const [searchTypes, setSearchTypes] = useState(
    getFiltersSavedOnSessionStorageByQueryId(searchQueryId, 'searchTypes'),
  );
  const [searchDates, setSearchDates] = useState(
    getFiltersSavedOnSessionStorageByQueryId(searchQueryId, 'dates'),
  );
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchId, setSavedSearchId] = useState(null);
  const [homeFeedSavedSearch, setHomeFeedSavedSearch] = useState(null);
  const [savedSearchName, setSavedSearchName] = useState(null);
  // Recent searches
  const [recentSearches, setRecentSearches] = useState([]);
  const [searchCountries, setSearchCountries] = useState(
    getFiltersSavedOnSessionStorageByQueryId(searchQueryId, 'countries'),
  );
  const [searchIndustries, setSearchIndustries] = useState(
    getFiltersSavedOnSessionStorageByQueryId(searchQueryId, 'industries'),
  );
  const [searchEntities, setSearchEntities] = useState(
    getFiltersSavedOnSessionStorageByQueryId(searchQueryId, 'entities'),
  );

  // Autocomplete suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setSuggestionsLoading] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ skip: 0, limit: 10 });

  // Extra filters
  const [extraFilter, setExtraFilter] = useState('');

  const { counts, fetchSearchCount, isCountLoading } = useSearchCounts();

  /*
   * These are our entity results
   * There is a slight difference in the structure if type is 'overview'
   * In that case we have up to 3 of each type - no pagination.
   * If type is not 'overview' we have 10 of a single - with pagination
   */
  const [isEntityResultsLoading, setEntityResultsLoading] = useState(false);
  const [entityResults, setEntityResults] = useState([]);
  const [aiEntitiSuggestions, setAiEntitiSuggestions] = useState([]);

  const [isFacetResultsLoading, setFacetResultsLoading] = useState(false);
  const [facetResults, setFacetResults] = useState([]);

  const isActiveSearch =
    activeSearch || // Search text is not empty
    searchTypes.length > 0 || // At least one entity type is selected
    searchIndustries.length > 0 || // At least one industry is selected
    searchEntities.length > 0 || // At least one entity is selected
    searchCountries.length > 0 || // At least one country is selected
    extraFilter; // Extra filter is selected

  const visibleResultType = getResultType(
    searchTypes.length > 0 ? searchTypes[0] : '',
  );

  const flatIndustries = useMemo(() => flattenTree(briefs), [briefs]);
  const isFilterOpen = params.get('isFilterOpen');

  const fetchFacetResults = async ({
    search = '',
    types,
    countries,
    industries,
    entities,
    pageInfo,
    dates,
  }) => {
    // Get the new facet results
    const variables = {
      name: search,
      filterIndustries: industries,
      filterEntities: entities,
      topics: countries,
      entities,
      bookmarked: extraFilter === 'bookmarks',
      connected: extraFilter === 'connections',
      notes: extraFilter === 'notes',
      compliance: extraFilter === 'compliant',
    };

    const [startDate, endDate] = dates;
    if (startDate && endDate) {
      variables.startDate = startDate;
      variables.endDate = endDate;
    }

    /*
     * We might not need to perform the query - if the activeGroup is not 'facet'
     * It means the user has selected a entity to focus on.
     * We'll perform this query for the totals information in the first page
     * But not in subsequent pages.
     * So if pageInfo.skip is not 0 (we are not on the first page) we'll skip the query
     */
    const isFirstPage = pageInfo.skip === 0;

    // For now we can only send one type at a time
    const activeType = types.length ? types[0] : '';
    const activeGroup = getResultType(activeType);

    if (activeGroup !== 'facet' && !isFirstPage) {
      setFacetResultsLoading(false);
      return;
    }

    if (activeType && activeGroup === 'facet') {
      variables.filterCategories = {
        events: {
          limit: 0,
          skip: 0,
        },
        regulations: {
          limit: 0,
          skip: 0,
        },
        quotes: {
          limit: 0,
          skip: 0,
        },
        [activeType]: pageInfo,
      };
    }

    sendEvent('advanced_search_facets_results', {
      description: 'Update search facets configuration',
      search,
      searchType: activeType,
      searchIndustries: industries,
      searchEntities: entities,
      topics: countries,
      pageInfo,
    });

    setFacetResultsLoading(true);

    try {
      const results = await fetchSearchFacetResults({
        client,
        ...variables,
      });

      // Hack, use a hook to ensure we have the latest search text
      let currentSearch = '';
      setSearchText((currentSearchText) => {
        currentSearch = currentSearchText;
        return currentSearchText;
      });

      if (search !== currentSearch) {
        setFacetResultsLoading(false);
        return;
      }

      if (!types.length) {
        setFacetResults(transformFacetSearchResult(results));
      } else {
        setFacetResults((currentResults) => {
          return mergeFacetSearchResults({
            currentResults,
            newResults: results,
            facetType: activeType,
          });
        });
      }
    } catch (error) {
      notify.error('There was an error retrieving events & regulations.');
    }
    setFacetResultsLoading(false);
  };

  const fetchEntityResults = async ({
    search,
    types,
    countries,
    entities,
    industries,
    pageInfo,
  }) => {
    // Get the new search results
    const variables = {
      name: search,
      topics: countries,
      filterEntities: entities,
      filterIndustries: industries,
      bookmarked: extraFilter === 'bookmarks',
      connected: extraFilter === 'connections',
      notes: extraFilter === 'notes',
      compliance: extraFilter === 'compliant',
    };

    /*
     * We might not need to perform the query - if the activeGroup is not 'entity'
     * It means the user has selected a facet to focus on.
     * We'll perform this query for the totals information in the first page
     * But not in subsequent pages.
     * So if pageInfo.skip is not 0 (we are not on the first page) we'll skip the query
     */
    const isFirstPage = pageInfo.skip === 0;

    // For now we can only send one type at a time
    const activeType = types.length ? types[0] : '';
    const activeGroup = getResultType(activeType);

    if (activeGroup !== 'entity' && !isFirstPage) {
      setEntityResultsLoading(false);
      return;
    }

    if (activeType && activeGroup === 'entity') {
      variables.filterCategories = { [activeType]: pageInfo };
    }

    /*
     * If user is seing All results in the filter
     * we must bring all entities under Actors section
     */
    if (!activeType) {
      variables.filterCategories = { actors: pageInfo };
    }

    if (search && search.length > 2) {
      setRecentSearches((recents) =>
        [search, ...recents.filter((r) => r !== search)].slice(0, 8),
      );
    }

    sendEvent('advanced_search_entities_results', {
      description: 'Update search configuration',
      search,
      searchType: activeType,
      searchCountries: countries,
      searchIndustries: industries,
      searchEntities: entities,
      pageInfo,
      topic: topicId,
    });

    setEntityResultsLoading(true);

    const shouldRunAiSearch =
      variables.filterEntities.length === 0 &&
      variables.filterIndustries.length === 0 &&
      variables.topics.length === 0 &&
      !activeType;

    const promises = [
      fetchSearchEntityResults({
        client,
        ...variables,
      }),
    ];

    if (shouldRunAiSearch) {
      promises.push(fetchAiSuggestions({ client, term: search }));
    }

    try {
      const [categories, aiSuggestions] = await Promise.allSettled(promises);

      setAiEntitiSuggestions(aiSuggestions?.value);
      /*
       * Set the results - we need to merge the results with what we already have
       * so we don't lose any of the old results (pagination).
       * this only applies for "type" filters (not overview)
       * We will update the "total" property provided by the search
       * And will merge the "entities" property provided by the search with our own.
       */

      // Hack, use a hook to ensure we have the latest search text
      let currentSearch = '';
      setSearchText((currentSearchText) => {
        currentSearch = currentSearchText;
        return currentSearchText;
      });

      if (search !== currentSearch) {
        setEntityResultsLoading(false);
        return;
      }

      if (!types.length) {
        setEntityResults(categories.value);
      } else {
        setEntityResults((currentResults) => {
          return mergeEntitySearchResults({
            currentResults,
            newResults: categories.value,
            entityType: activeType,
          });
        });
      }
      // If the options array is empty, set it again.
    } catch (error) {
      notify.error('There was an error retrieving the search results.');
    }
    setEntityResultsLoading(false);
  };

  const getIndustriesAndCountriesSuggestions = (searchTerm) => {
    const query = searchTerm.substring(1);
    const filteredIndustries = flatIndustries.filter(({ name }) =>
      name.toLowerCase().startsWith(query.toLowerCase()),
    );

    const filteredCountries = subscribedCountries.filter(({ name }) =>
      name.toLowerCase().includes(query.toLowerCase()),
    );

    const countriesSuggestions = filteredCountries.map((country) => ({
      id: country.id,
      name: country.name,
      briefType: 'country',
    }));

    const industriesSuggestions = filteredIndustries.map((industry) => ({
      id: industry.id,
      name: industry.name,
      briefType: 'industry',
    }));

    return [...industriesSuggestions, ...countriesSuggestions].slice(0, 6);
  };

  const fetchAutocompleteResults = async ({
    search,
    countries,
    targetEntities,
  }) => {
    let symbol = '';
    let term = search;

    const isIndustryOrCountryFilter = search.startsWith('#');

    if (isIndustryOrCountryFilter || search.startsWith('@')) {
      symbol = search.substring(0, 1);
      term = search.substring(1);
    }

    if (!term && symbol === '@') {
      setSuggestionsLoading(false);
      return;
    }

    const variables = {
      term,
      symbol,
      topics: countries,
      entities: targetEntities,
    };

    sendEvent('advanced_search_autocomplete', {
      description: 'Autocomplete request search',
      search,
      term,
      symbol,
    });

    setSuggestionsLoading(true);

    try {
      if (isIndustryOrCountryFilter) {
        const briefSuggestions = getIndustriesAndCountriesSuggestions(search);
        setSuggestions(briefSuggestions);
        setSuggestionsLoading(false);
        return;
      }

      const result = await fetchAutocomplete({
        client,
        ...variables,
      });

      const { entities = [] } = result;

      // Hack, use a hook to ensure we have the latest search text
      let currentSearch = '';
      setSearchText((currentSearchText) => {
        currentSearch = currentSearchText;
        return currentSearchText;
      });

      if (search !== currentSearch) {
        setSuggestionsLoading(false);
        return;
      }

      setSuggestions(entities);
    } catch (error) {
      notify.error('There was an error retrieving the suggestions.');
    }
    setSuggestionsLoading(false);
  };

  const onToggleFilterClicked = () => {
    params.delete('leftPanel');
    params.set('isFilterOpen', true);
    replaceParams(params);
  };

  const applySavedSearchFilter = (savedSearch) => {
    setSavedSearchId(savedSearch.id);
    setSavedSearchName(savedSearch.name);

    const searchData = JSON.parse(savedSearch.search_data);

    setHomeFeedSavedSearch(searchData.isHomeFeed ? savedSearch : null);
    setSearchEntities(searchData.filterEntities);
    setSearchCountries(searchData.topics);
    setSearchIndustries(searchData.filterIndustries);
    setActiveSearch(searchData.name);
    setSearchTypes(searchData.filterCategories);
    setSearchDates(searchData.searchDates);
  };

  // Use the search URL parameter to get some results
  useEffect(() => {
    setSearchText(searchQuery || '');
    setActiveSearch(searchQuery || '');
  }, [searchQuery]);

  useEffect(() => {
    setSuggestions([]);
  }, [searchText]);

  // We only show the date filter if the user is dealing with "facet"
  useEffect(() => {
    if (visibleResultType !== 'facet') {
      setSearchDates([null, null]);
    }
  }, [visibleResultType]);

  /*
   * If we modify the search industries we need to invalidate the data for entities
   * We also need to reset the pagination.
   * We set the loading to true - since we are clearing the results.
   * This is to avoid a bad UI between debounce times!
   */
  useEffect(() => {
    setPagination({ skip: 0, limit: 10 });
    setEntityResultsLoading(true);
    setFacetResultsLoading(true);
    setEntityResults([]);
    setFacetResults([]);
    setAiEntitiSuggestions([]);
  }, [
    searchCountries,
    searchIndustries,
    searchEntities,
    activeSearch,
    searchTypes,
    extraFilter,
  ]);

  /*
   * If we modify the searchDates or the extra filters we only invalidate the data for facets
   * Entities are timeless and are not affected by the query, so we don't clear them
   */
  useEffect(() => {
    setPagination({ skip: 0, limit: 10 });
    setFacetResultsLoading(true);
    setFacetResults([]);
  }, [searchDates, extraFilter]);

  /*
   * If we modify the pagination then we need to fetch new results.
   * But if we dont have a search text we don't and we set loading to false.
   * (It may have been set to "true" to avoid a bad UI between debounce times)
   * Same logic goes for the event pagination.
   */
  useEffect(() => {
    if (isActiveSearch || pagination.skip > 0) {
      fetchEntityResults({
        search: activeSearch,
        types: searchTypes,
        countries: searchCountries.map((c) => c.id),
        entities: searchEntities.map((e) => e.id),
        industries: searchIndustries.map((i) => i.id),
        pageInfo: pagination,
      });
      fetchFacetResults({
        search: activeSearch,
        types: searchTypes,
        countries: searchCountries.map((c) => c.id),
        entities: searchEntities.map((e) => e.id),
        industries: searchIndustries.map((i) => i.id),
        pageInfo: pagination,
        dates: searchDates,
      });
    } else {
      setEntityResultsLoading(false);
      setFacetResultsLoading(false);
    }
  }, [pagination]);

  /*
   * If we modify the search text and it's an autocomplete type of
   * search - then we need to fetch new autocomplete results.
   */
  useEffect(() => {
    fetchAutocompleteResults({
      search: debounceSearchText,
      countries: searchCountries.map((c) => c.id),
      targetEntities: searchEntities.map((e) => e.id),
    });
  }, [
    debounceSearchText,
    searchEntities.map((e) => e.id).length,
    searchCountries.map((c) => c.id).length,
  ]);

  useEffect(() => {
    fetchSearchCount({
      search: activeSearch,
      industries: searchIndustries.map((i) => i.id),
      countries: searchCountries.map((c) => c.id),
      entities: searchEntities.map((e) => e.id),
      extraFilter,
      dates: searchDates,
      client,
    });
  }, [
    activeSearch,
    searchIndustries,
    searchCountries,
    searchEntities,
    searchDates,
  ]);

  // Apply saved search when user clicks on leftPanel
  useEffect(() => {
    if (!selectedSavedSearch) return;

    applySavedSearchFilter(selectedSavedSearch);
  }, [selectedSavedSearch]);

  // Remove saved search when unMounting component
  useEffect(() => () => setSelectedSavedSearch(null), []);

  return (
    <SearchContext.Provider
      value={{
        topicId,

        recentSearches,

        activeSearch,
        searchText,
        searchTypes,
        searchCountries,
        searchIndustries,
        searchEntities,
        searchDates,

        isEntityResultsLoading,
        isFacetResultsLoading,

        suggestions,
        isSuggestionsLoading,

        entityResults,
        facetResults,

        visibleResultType,
        extraFilter,
        savedSearchId,
        homeFeedSavedSearch,
        savedSearchName,

        counts,
        isCountLoading,

        aiEntitiSuggestions,

        savedSearches,
        setSavedSearches,

        setActiveSearch,
        setSearchText,
        setSearchTypes,
        setSearchCountries,
        setSearchIndustries,
        setSearchEntities,
        setSearchDates,
        setPagination,
        setExtraFilter,
        setSavedSearchId,
        setSavedSearchName,
        setHomeFeedSavedSearch,
      }}
    >
      <div data-cy="search" className={css.main}>
        <aside className={css.filters} data-open={isFilterOpen}>
          <SidebarHeader onClose={() => deleteParam('isFilterOpen')} />
          <SearchFilters />
        </aside>

        <aside className={css.toggleFilter}>
          <ToggleSideNav
            filterStatus={isFilterOpen ? 'expanded' : 'closed'}
            onClick={onToggleFilterClicked}
          />
        </aside>
        <div
          data-dossier={dossierType ? 'open' : 'closed'}
          data-open={isFilterOpen}
          className={css.content}
        >
          <div className={css.header}>
            <SearchBar />
            {isActiveSearch && <SearchHeader />}
          </div>
          <div className={css.results}>
            {isActiveSearch && <SearchResults />}
          </div>
        </div>
      </div>
    </SearchContext.Provider>
  );
};

export default Search;

export const useSearch = () => useContext(SearchContext);
